// Product dosage text joins clauses with "; " ("...until symptoms controlled; Maximum daily dose 3.0g")
const CLAUSE_SEPARATOR = '; ';
const ENDS_WITH_PUNCTUATION = /[.,;:!?]$/;

/**
 * Joins the phrases shown as chips into the one suffix written to the dosage text, in chip order. Undefined when
 * there is no phrase, so callers can treat "no suffix" and "no phrases" alike.
 */
export const joinDosageSuffix = (phrases: { phrase: string }[]) =>
  phrases.length ? phrases.map(({ phrase }) => phrase).join(CLAUSE_SEPARATOR) : undefined;

/** Appends the suffix phrase to dosage text as its final clause, so the chips' phrases reach the prescription */
export const appendDosageSuffix = (dosage: string | undefined, suffix: string) => {
  const text = dosage?.trim() ?? '';
  if (!text) {
    return suffix;
  }
  // At the review step the stored dosage already carries the suffix and is validated again
  if (text.endsWith(suffix)) {
    return text;
  }
  const separator = ENDS_WITH_PUNCTUATION.test(text) ? ' ' : CLAUSE_SEPARATOR;
  return `${text}${separator}${suffix}`;
};

/**
 * Characters `appendDosageSuffix` adds to the text, so a length limit on the typed text can leave room for the
 * suffix.
 */
export const getDosageSuffixLength = (dosage: string, suffix: string) =>
  appendDosageSuffix(dosage, suffix).length - dosage.trim().length;

/**
 * Inverse of `appendDosageSuffix`, removing only the separator added with the suffix. It cannot restore a trailing ";"
 * the prescriber typed ("take one;" joins as "take one; Phrase") or trailing whitespace. Nor typed text that already
 * ended in the suffix, which `appendDosageSuffix` takes as written: "Do not exceed Maximum daily dose 3 g" comes back
 * from the review step as "Do not exceed".
 */
export const removeDosageSuffix = (dosage: string | undefined, suffix: string) => {
  // Every text ends with "", and slice(0, -0) is empty, so an empty suffix would wipe the dosage
  if (!suffix || !dosage?.endsWith(suffix)) {
    return dosage;
  }
  const text = dosage.slice(0, -suffix.length);
  if (text.endsWith(CLAUSE_SEPARATOR)) {
    return text.slice(0, -CLAUSE_SEPARATOR.length);
  }
  return text.trimEnd();
};
